import NeonHero from '../components/NeonHero';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import fastlaneBackshot from '../assets/fastlaneBackshot.jpg';
import fastlanePartDetection from '../assets/fastlanePartDetection.jpg';
import fastlaneRear from '../assets/fastlaneRear.jpg';
import fastlaneSide from '../assets/fastlaneSide.jpg';
import frontlineManagement from '../assets/frontlineManagement.jpg';
import frontlineNavigation from '../assets/frontlineNavigation.png';
import frontlineWallet from '../assets/frontlineWallet.png';
import ubiquityRealm from '../assets/ubiquityRealm.png';

export default function Solutions() {
	return (
		<div>
			<NeonHero
				title="Technologies powering Ubiquity"
				subtitle="From automated damage assessments to technician coordination and a real‑time customer portal, our stack delivers speed, quality, and transparency."
				primaryCta={{ label: 'Open Realm', href: 'https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5' }}
			/>

			{/* FastLane */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12"
			>
				{/* Image grid above title */}
				<motion.div 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6 }}
					className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
				>
					{[fastlaneBackshot, fastlanePartDetection, fastlaneRear, fastlaneSide].map((src, i) => (
						<motion.div 
							key={i} 
							initial={{ opacity: 0, scale: 0.8 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: i * 0.1 }}
							whileHover={{ scale: 1.05 }}
							className="rounded-xl overflow-hidden border bg-white"
						>
							<img src={src} alt="FastLane" className="block w-full h-40 md:h-44 object-cover" />
						</motion.div>
					))}
				</motion.div>
				<motion.h2 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl font-bold"
				>
					FastLane
				</motion.h2>
				<motion.p 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="mt-2 text-gray-700"
				>
					Automates the heavy lifting from first touch through completion so customers get an instant path to repair.
				</motion.p>
				<div className="mt-6 grid md:grid-cols-2 gap-6">
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Automated Damage Assessments</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Classifies damage from photos/3D capture</li>
							<li>Suggests repair vs replacement with confidence</li>
							<li>Produces estimate primitives instantly</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Vehicle Diagnostics, Calibrations & Coding</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>OBD and subsystem checks with guided tests</li>
							<li>ADAS calibration loop with validation gates</li>
							<li>OEM‑aligned coding/reprogramming steps</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm md:col-span-2">
						<h3 className="font-semibold">Parts & Service Procurement</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Real‑time price benchmarking across vendor networks</li>
							<li>Smart substitutions and availability awareness</li>
							<li>Auto‑routing to mobile or hub based on complexity</li>
						</ul>
					</div>
				</div>
			</motion.section>

			{/* FrontLine */}
			<section className="container py-12">
				{/* Device mock gallery above title */}
				<div className="mb-6 flex gap-4 overflow-x-auto pb-2">
					{[
						{ src: frontlineManagement, alt: 'FrontLine management' },
						{ src: frontlineNavigation, alt: 'FrontLine navigation' },
						{ src: frontlineWallet, alt: 'FrontLine wallet' },
					].map((img) => (
						<div key={img.alt} className="shrink-0 w-56 md:w-64 bg-black rounded-[2rem] p-3 shadow-xl border border-black/20">
							<div className="aspect-[9/19.5] rounded-[1.5rem] overflow-hidden">
								<img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
							</div>
						</div>
					))}
				</div>
				<h2 className="text-2xl font-bold">FrontLine (Technician App)</h2>
				<p className="mt-2 text-gray-700">A complete field experience—from onboarding to daily execution—so technicians can deliver safe, high‑quality work with total transparency.</p>
				<div className="mt-6 grid md:grid-cols-3 gap-6">
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Onboarding</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Background check and identity verification</li>
							<li>Link bank account for payouts</li>
							<li>Compliance setup (W‑9/1099), tax & mileage tracking</li>
							<li>Rent a fully equipped truck if needed</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Workday</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Order management with live updates</li>
							<li>Pre‑inspection with photos and 3D modelling</li>
							<li>Diagnostics, procedures, and QA checkpoints</li>
							<li>Real‑time coordination with customers and hubs</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Payments & Compliance</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Milestone‑based payouts and tips</li>
							<li>Safety attestations and procedure verification</li>
							<li>Audit trails tied to each job event</li>
						</ul>
					</div>
				</div>
			</section>

			{/* The Realm */}
			<section className="container py-12">
				{/* Desktop monitor mock above title */}
				<div className="mb-6 mx-auto w-full max-w-5xl rounded-2xl bg-black p-3 shadow-xl border border-black/20">
					<div className="aspect-video rounded-lg overflow-hidden bg-black">
						<img src={ubiquityRealm} alt="The Realm portal" className="w-full h-full object-cover object-top" />
					</div>
				</div>
				<h2 className="text-2xl font-bold">The Realm (Fleet Management Portal)</h2>
				<p className="mt-2 text-gray-700">A comprehensive B2B portal designed for fleet accounts to manage vehicles, services, and operations with locked-in discounted rates and dedicated API integration.</p>
				<div className="mt-6 grid md:grid-cols-3 gap-6">
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Fleet Account Benefits</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Locked-in discounted rates on glass parts and services</li>
							<li>Dedicated API for fleet management integration</li>
							<li>Vehicle status, history, and damage assessment</li>
							<li>Pre-inspection and 3D model generation</li>
							<li>Comprehensive service information tracking</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Management Capabilities</h3>
						<ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
							<li>Location, user, and order management</li>
							<li>Service scheduling and technician coordination</li>
							<li>Analytics software integration</li>
							<li>Ubiquity subscription management</li>
							<li>Multi-account fleet oversight</li>
						</ul>
					</div>
					<div className="rounded-xl border bg-white p-6 shadow-sm">
						<h3 className="font-semibold">Access Portal</h3>
						<p className="mt-2 text-gray-700">Both individual customers and B2B fleet accounts log into The Realm to service personal vehicles and manage fleet operations.</p>
						<div className="mt-3"><a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button variant="gradient">Open Realm</Button></a></div>
					</div>
				</div>
			</section>

			<section className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white">
					<h3 className="text-2xl font-bold">Build on Ubiquity</h3>
					<p className="mt-2 max-w-2xl">Reduce cycle times and deliver a transparent, premium customer experience with our platform.</p>
					<div className="mt-6 flex flex-wrap gap-3">
					<a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90 dark:bg-transparent dark:border dark:border-white dark:text-white dark:hover:bg-white/10">Open Realm</Button></a>
						<a href="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Sales</Button></a>
					</div>
				</div>
			</section>
		</div>
	);
}
